import { useEffect, useRef } from 'react';
import { Upload, FileSpreadsheet, X } from 'lucide-react';

interface FileDropZoneProps {
  /** Unique id wired to the hidden <input> and its <label htmlFor>. */
  inputId: string;
  /** Currently selected file, or null when none is chosen. */
  file: File | null;
  /** Called with the picked/dropped File. The caller owns validation. */
  onFile: (file: File) => void;
  /** Called when the user clears the selected file via the X button. */
  onClear: () => void;
}

/**
 * Shared dashed drag-and-drop file picker used by the bulk-upload modals.
 * Validation is intentionally left to the caller's onFile handler.
 */
export default function FileDropZone({ inputId, file, onFile, onClear }: FileDropZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep the native input value in sync when the caller clears the file
  // (e.g. on modal close) so re-picking the same file still fires onChange.
  useEffect(() => {
    if (!file && fileInputRef.current) fileInputRef.current.value = '';
  }, [file]);

  const handleClear = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div
      className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
        file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
      }`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handlePick}
        className="hidden"
        id={inputId}
      />
      {file ? (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2">
            <FileSpreadsheet className="h-8 w-8 text-green-600" />
            <span className="text-sm font-medium text-green-700">{file.name}</span>
            <button
              onClick={handleClear}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
        </div>
      ) : (
        <label htmlFor={inputId} className="cursor-pointer">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-3 text-sm font-medium text-gray-700">
            Drag and drop your file here, or{' '}
            <span className="text-primary-600 hover:text-primary-700">browse</span>
          </p>
          <p className="mt-1 text-xs text-gray-500">Supports .xlsx, .xls, and .csv files</p>
        </label>
      )}
    </div>
  );
}
